import _ from "lodash";
import { action, autorun, computed, observable } from "mobx";
import { UserInfo } from "./UserInfo";
import { wsConfirmDialog } from "./wsConfirmDialog";

export interface UnsavedChangeParams {
  key?: string | number;
  type?: string;
  needsConfirmationBeforeUnload: () => boolean;
  needsConfirmationBeforeSpaRedirect: (newUrl: string, state: any) => boolean;

  /**
   * UnsavedChanges in the front of the queue with
   * identitcal concurrentGroup can all fire at the same time
   */
  concurrentGroup?: string;
}

export interface UnsavedChange extends UnsavedChangeParams {
  key: string | number;
}

let _usavedChangeKey = 0;
let _uniqueMutationGroupId = 0;

export const unsavedChange = (params: UnsavedChangeParams) => {
  const { key, concurrentGroup, ...rest } = params;

  return {
    key: key ?? ++_usavedChangeKey,
    concurrentGroup:
      concurrentGroup ?? `uniqueMutationGroup${++_uniqueMutationGroupId}`,
    ...rest,
  };
};

export const _unsavedChanges = observable.array<UnsavedChange>();
export const _unsavedChangesGetters = observable.array<() => UnsavedChange[]>();

export const unsavedChanges = computed(() => {
  const array = [..._unsavedChanges];

  const unsavedChanges = _unsavedChangesGetters.map((g) => g());
  unsavedChanges.forEach((uc) => {
    array.push(...uc);
  });

  return array;
});

export const addUnsavedChange = action((_uc: UnsavedChangeParams) => {
  const uc = unsavedChange(_uc);
  if (!_unsavedChanges.find((x) => x.key === uc.key)) {
    _unsavedChanges.push(uc);
  }

  return action(() => {
    _.remove(_unsavedChanges, (x) => x.key === uc.key);
  });
});

export const addUnsavedChangeGetter = action(
  (getter: () => UnsavedChange[]) => {
    if (!_unsavedChangesGetters.find((x) => x === getter)) {
      _unsavedChangesGetters.push(getter);
    }

    return action(() => {
      _unsavedChangesGetters.remove(getter);
    });
  }
);

export interface AllowSpaRedirectDialogProps {
  userInfo: UserInfo;
  unsavedChange: UnsavedChange;
  newUrl: string;
  newState: any;
}
export const allowSpaRedirectDialog = ({
  userInfo,
  unsavedChange,
  newUrl,
  newState,
}: AllowSpaRedirectDialogProps) => {
  if (!unsavedChange.needsConfirmationBeforeSpaRedirect(newUrl, newState)) {
    return true;
  }

  return wsConfirmDialog({
    userInfo: userInfo,
    header: userInfo.locStore
      .id("5bb04c2afa91")
      .loc("Vil navigere vekk fra skjemaet?"),
    message: userInfo.locStore
      .id("c99fb5f45a3d")
      .loc("Det kan hende endringene dine ikke er lagret"),
    acceptLabel: userInfo.locStore.id("2b053e755f50").loc("Naviger vekk"),
    rejectLabel: userInfo.locStore.id("06ac1583bc5c").loc("Avbryt"),
    icon: "pi pi-save",
  });
};

let beforeUnloadListener;
// (try to) prevent unloading page when there are unsaved changes / mutations pending
autorun(() => {
  const needsConfirmation = unsavedChanges
    .get()
    .filter((uc) => uc.needsConfirmationBeforeUnload());

  const needListener = needsConfirmation.length > 0;

  const hasListener = !!beforeUnloadListener;

  if (needListener && !hasListener) {
    beforeUnloadListener = (event: BeforeUnloadEvent) => {
      if (window.cvpIsLiveReloading) {
        return;
      }

      event.preventDefault();
      return (event.returnValue =
        "Changes may not be saved yet. Are you sure you want to exit?");
    };

    window.addEventListener("beforeunload", beforeUnloadListener, {
      capture: true,
    });
  }

  if (!needListener && hasListener) {
    window.removeEventListener("beforeunload", beforeUnloadListener, {
      capture: true,
    });

    beforeUnloadListener = null;
  }
});
